// CREATING SOMETHING OUT OF THE EMAIL BEING READ (busy-my-web#517, Fatemeh's "open email example
// - create a task" frames 40011598:356726 → 40011618:361324).
//
// With a thread open in the middle the box's suggestion half asks "What would you like to do?" and
// offers the three creates the frames draw — "Add to Tasks (Recommended) / Add to Notes / Create
// Event". Picking one arms a text "Add" where the send arrow was; Add opens the matching creation
// form already filled from the mail (its subject as the title, its latest message as the body),
// and the created thing lands on the thread's own timeline — which is what puts the "Task created"
// row on the Email timeline rail and the count in the Briefing header.
//
// Pure, like the rest of lib/emailSession.js: the strings, the option rows, the prefill and the
// receipt are all answerable without a store, so the wording cannot drift between the chooser, the
// CTA, the strip and the rail.

import { senderDisplayName } from '@/lib/senderDisplay'

/** Verbatim design strings. */
export const THREAD_CREATE_TEXT = {
  addToTasks: 'Add to Tasks',
  addToNotes: 'Add to Notes',
  createEvent: 'Create Event',
  add: 'Add',
  // Four dots in the frame (40011598:356726), not an ellipsis: the frame's own spelling.
  askPlaceholder: 'Ask about this email....',
  close: 'Close',
}

/** The three options' ids — one prefix, so `threadCreateMenu` can tell them from every other armed id. */
export const THREAD_CREATE_IDS = {
  tasks: 'thread-create-task',
  notes: 'thread-create-note',
  events: 'thread-create-event',
}

/** The window event the session's commit raises and the box listens for. */
export const THREAD_CREATE_EVENT = 'smartbox-thread-create'

const VERBS = { tasks: 'Task created', notes: 'Note added', events: 'Event created' }

/** The chooser's rows for an open thread: tasks recommended, the other two plain. All ready. */
export function threadCreateOptions() {
  return [
    { id: THREAD_CREATE_IDS.tasks, text: THREAD_CREATE_TEXT.addToTasks, recommended: true, ready: true },
    { id: THREAD_CREATE_IDS.notes, text: THREAD_CREATE_TEXT.addToNotes, recommended: false, ready: true },
    { id: THREAD_CREATE_IDS.events, text: THREAD_CREATE_TEXT.createEvent, recommended: false, ready: true },
  ]
}

/** Which creation menu an armed id opens — null for every id that is not one of the three. */
export function threadCreateMenu(armed) {
  return Object.keys(THREAD_CREATE_IDS).find(menu => THREAD_CREATE_IDS[menu] === armed) || null
}

/** The "Add" CTA the armed option puts in the action bar; null when nothing of ours is armed. */
export function threadCreateCta(armed, { running = false } = {}) {
  if (!threadCreateMenu(armed)) return null
  return { kind: 'thread-create', label: THREAD_CREATE_TEXT.add, disabled: !!running }
}

// Block ends become a space (two paragraphs stay two sentences); inline tags vanish ("<b>documentation</b>." keeps its full stop).
const stripTags = (html) => String(html || '').replace(/<\/(p|div|li|h\d)>|<br\s*\/?>/gi, ' ').replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()

/** The newest message on the thread, by arrival — the one the form is written from. */
function latestMessage(thread) {
  const messages = (thread?.emailMessages || []).filter(m => m && m.isDeleted !== true)
  return messages.reduce((best, m) => {
    const t = new Date(m.receivedUtc || m.sentUtc || m.createDate || 0).getTime() || 0
    const bt = new Date(best?.receivedUtc || best?.sentUtc || best?.createDate || 0).getTime() || 0
    return !best || t >= bt ? m : best
  }, null)
}

/**
 * What the form opens with (frame 40011618:359072 — "Design Busy.me" over "Review product
 * documentation and design based on changes that Mr. Saffet mentions"): the thread's subject as
 * the title, and the latest message's text as the body, prefixed with who wrote it. `thread` is
 * the canonical timeline record (`timeline/getTimelineById`) or the session's `{ id, subject }`.
 */
export function threadCreatePrefill(thread) {
  if (!thread) return null
  const title = String(thread.subject || thread.headLine || '').trim()
  const m = latestMessage(thread)
  const body = stripTags(m?.bodyText || m?.textBody || m?.previewText || m?.preview || thread.previewText || '')
  const who = m ? senderDisplayName(m.senders || m.sender || m.from || '') : ''
  const content = body ? `<p>${who ? `${who}: ` : ''}${body}</p>` : ''
  return { title, content, timelineId: thread.id || null }
}

/** The strip after a create from an open email (frame 40011618:361324): "Task created · <title>". */
export function threadCreatedReceipt(menu, title) {
  const verb = VERBS[menu] || 'Saved'
  const name = String(title || '').trim()
  return name ? `${verb} · ${name}` : verb
}

/** The rail's "Due Tomorrow, 8pm" line under a task or event raised from the thread. */
export function dueLine(record, stamp) {
  const due = record?.dueDate || record?.startDate || null
  if (!due) return ''
  const { date, time } = stamp(due)
  if (!date) return ''
  return time ? `Due ${date}, ${time}` : `Due ${date}`
}
